import type { NodeViewProps, ReactNodeViewProps } from '@tiptap/react'
import type {
  NodeComponentProps,
  NodeViewLiveProps,
  NodeViewStaticProps,
} from '../tiptap-helpers'
import type { MentionNodeAttributes } from './MentionNode'

import { useRundownSnap } from '@/features/rundown/rundown-page.store'
import { RundownPermission } from '@rundown-studio/types'
import { useRundownPermission } from '@/features/permissions/lib/use-rundown-permission'
import { modalActions } from '@/features/rundown/modals/modal.store'

import { NodeViewWrapper } from '@tiptap/react'
import { useEffect } from 'react'

import { useMention, type MentionDto } from '@/entities/mentions'
import { MentionDescPreview } from '@/features/rundown/mentions/MentionDescPreview'

import {
  Popover,
  PopoverAction,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { PencilIcon } from 'lucide-react'
import { MentionChip, MentionChipLayout } from './MentionChip'

export function MentionNodeViewLive({
  node,
  updateAttributes,
  ...props
}: NodeViewLiveProps) {
  const attrs = node.attrs as MentionNodeAttributes

  const { id: rundownId } = useRundownSnap()

  const { data: mention } = useMention({
    params: {
      rundownId,
      mentionId: attrs.mentionId,
    },
    options: {
      enabled: !rundownId,
    },
  })

  useEffect(() => {
    if (mention) {
      updateAttributes({
        fallbackName: mention.name,
      })
    }
  }, [mention, updateAttributes])

  return (
    <MentionNodeComponent node={node} HTMLAttributes={props.HTMLAttributes} />
  )
}

export function MentionNodeViewStatic({ node }: NodeViewStaticProps) {
  return <MentionNodeComponent node={node} />
}

function MentionNodeComponent({
  node,
  HTMLAttributes,
}: NodeComponentProps & {
  HTMLAttributes?: NodeViewProps['HTMLAttributes']
}) {
  const attrs = node.attrs as MentionNodeAttributes

  const { id: rundownId } = useRundownSnap()

  const {
    data: mention,
    isLoading,
    isSuccess,
    isError,
  } = useMention({
    params: {
      rundownId,
      mentionId: attrs.mentionId,
    },
    options: {
      enabled: !rundownId,
    },
  })

  const hasWriteAccess = useRundownPermission({
    permission: RundownPermission.EDIT,
  })

  if (isError || !attrs.mentionId || !isSuccess) {
    return (
      <MentionNodeLayout {...HTMLAttributes}>
        <span>@{attrs.fallbackName}</span>
      </MentionNodeLayout>
    )
  }

  if (isLoading) {
    return (
      <MentionNodeLayout {...HTMLAttributes}>
        <MentionChipLayout
          className="bg-animated from-gray-700 to-gray-900"
          name="Loading..."
        />
      </MentionNodeLayout>
    )
  }

  return (
    <MentionNodeLayout {...HTMLAttributes}>
      <Popover>
        <PopoverTrigger
          nativeButton={false}
          tabIndex={-1}
          render={<MentionChip mention={mention} className="cursor-help" />}
        />
        <PopoverContent initialFocus={false} className="w-80">
          <PopoverHeader>
            <PopoverTitle>Mention</PopoverTitle>

            <PopoverAction>
              <Button
                aria-label="Edit mention"
                variant="ghost"
                size="icon-sm"
                disabled={!hasWriteAccess}
                onClick={() => modalActions.open('MENTIONS', {})}
              >
                <PencilIcon />
              </Button>
            </PopoverAction>
          </PopoverHeader>

          <div className="space-y-2">
            <MentionChip mention={mention} />

            <div className="max-h-[70vh] overflow-y-auto">
              <MentionPopoverDescription mention={mention} />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </MentionNodeLayout>
  )
}

function MentionNodeLayout(
  props: React.PropsWithChildren &
    ReactNodeViewProps<HTMLDivElement>['HTMLAttributes'],
) {
  return (
    <NodeViewWrapper
      {...props.HTMLAttributes}
      as="span"
      ref={props.ref}
      className="inline-block align-baseline"
    >
      {props.children}
    </NodeViewWrapper>
  )
}

function MentionPopoverDescription({ mention }: { mention: MentionDto }) {
  if (!mention.description?.length || mention.description === '<p></p>') {
    return (
      <p className="px-2 py-1 text-sm italic opacity-50">
        No description added.
      </p>
    )
  }

  return <MentionDescPreview content={mention.description} />
}
